import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { useTranslation } from 'react-i18next'
import NavbarNew from 'components/navbar-new'
import { Container } from 'react-bootstrap'
import { LOGIN } from 'routes/routeConstant'
import { loginWithEmailLink } from '../../../redux/actions/authAction'
import { loginUserWithOtp } from '../../../redux/actions/userAction'
import { safeNext } from './safeNext'
import '../auth.css'

/**
 * Landing page for the auto-login links in our emails: /auth/e?t=<token>&next=/stories
 *
 * A learner who is already signed in skips redemption entirely and goes straight through, so
 * re-opening an old email never disturbs a live session. Everyone else is logged in here and
 * forwarded to the page the email pointed at, instead of being dropped on an OTP form.
 *
 * Redemption is a POST from this page rather than a GET the mail client could follow, so link
 * scanners that fetch every URL in an inbound email cannot consume the token first.
 */
const EmailLinkLogin = () => {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [failed, setFailed] = useState(false)
  // React 18 StrictMode mounts effects twice in development; without this the token is redeemed
  // twice and two sessions are minted for one click.
  const startedRef = useRef(false)

  const token = params.get('t')
  const next = safeNext(params.get('next'))

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const alreadyAuthed = Boolean(localStorage.getItem('authToken'))
    if (alreadyAuthed || !token) {
      if (!token && !alreadyAuthed) {
        setFailed(true)
        return
      }
      navigate(next, { replace: true })
      return
    }

    const redeem = async () => {
      const response: any = await loginWithEmailLink(token)

      if (response?.status === 200 && response?.data) {
        loginUserWithOtp({ userDetails: response.data })
        try {
          posthog.capture('email_link_login', { next })
        } catch {
          /* analytics must never block the login */
        }
        navigate(next, { replace: true })
        return
      }

      setFailed(true)
    }

    redeem()
  }, [token, next, navigate])

  if (failed) {
    return (
      <div className='auth_bg_wrapper'>
        <NavbarNew />
        <Container className='middle_contains_div'>
          <div className='auth-page-wrapper'>
            <div className='auth-modal-box'>
              <div className='auth-header-div'>
                <h2>{t('emailLink.expiredTitle')}</h2>
                <p>{t('emailLink.expiredBody')}</p>
              </div>
              <div className='auth-button-div'>
                <button
                  type='button'
                  className='button-2 green-btn-class w-button w-100'
                  onClick={() => navigate(LOGIN, { replace: true, state: { from: next } })}
                >
                  {t('emailLink.logIn')}
                </button>
              </div>
            </div>
          </div>
        </Container>
      </div>
    )
  }

  return (
    <div className='auth_bg_wrapper'>
      <NavbarNew />
      <Container className='middle_contains_div'>
        <div className='auth-page-wrapper'>
          <div className='auth-modal-box'>
            <div className='auth-header-div'>
              <h2>{t('emailLink.signingInTitle')}</h2>
              <p>{t('emailLink.signingInBody')}</p>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default EmailLinkLogin
